/**
 * 快捷键表示与匹配（M2-01 / DESIGN.md §7.4）。
 *
 * 快捷键**完全兼容 Photoshop 默认值**——这是降低迁移成本最有效的一招（§7.4）。
 * 单字母键（V/M/B…）属于工具切换，归工具系统（M2-05）；这里管的是带修饰键的
 * 动作快捷键（Ctrl+Z、Ctrl+Shift+I…）。
 *
 * `mod` 统一抽象「平台主修饰键」：Windows/Linux 的 Ctrl、macOS 的 ⌘。
 * 匹配时二者等价，显示时按平台各自格式化。
 */

export interface Shortcut {
  /** KeyboardEvent.key，字母一律大写存储。 */
  readonly key: string;
  /** 平台主修饰键（Ctrl / ⌘）。 */
  readonly mod?: boolean;
  readonly shift?: boolean;
  readonly alt?: boolean;
}

/** 匹配所需的最小事件面。真实 KeyboardEvent 满足它。 */
export interface KeyEventLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

export function matchesShortcut(shortcut: Shortcut, event: KeyEventLike): boolean {
  const mod = event.ctrlKey || event.metaKey;
  return (
    event.key.toUpperCase() === shortcut.key.toUpperCase() &&
    mod === (shortcut.mod === true) &&
    event.shiftKey === (shortcut.shift === true) &&
    event.altKey === (shortcut.alt === true)
  );
}

/** 两个快捷键组合是否是同一个按键（M5-03 快捷键编辑器的冲突检测用）。 */
export function shortcutsEqual(a: Shortcut, b: Shortcut): boolean {
  return (
    a.key.toUpperCase() === b.key.toUpperCase() &&
    (a.mod === true) === (b.mod === true) &&
    (a.shift === true) === (b.shift === true) &&
    (a.alt === true) === (b.alt === true)
  );
}

/**
 * 从一次真实按键事件录制出 {@link Shortcut}——快捷键编辑器"点击录制新组合键"
 * 用。单独一个纯修饰键（只按了 Ctrl，还没按下真正的键）不构成合法快捷键，
 * 返回 null，调用方据此判断"还在等用户按下主键"。
 */
export function shortcutFromEvent(event: KeyEventLike): Shortcut | null {
  const MODIFIER_KEYS = new Set(['Control', 'Meta', 'Shift', 'Alt']);
  if (MODIFIER_KEYS.has(event.key)) return null;
  const shortcut: Shortcut = {
    key: event.key.toUpperCase(),
    ...(event.ctrlKey || event.metaKey ? { mod: true } : {}),
    ...(event.shiftKey ? { shift: true } : {}),
    ...(event.altKey ? { alt: true } : {}),
  };
  return shortcut;
}

export type Platform = 'mac' | 'win';

export function detectPlatform(
  nav: { platform?: string } | undefined = globalThis.navigator,
): Platform {
  return typeof nav?.platform === 'string' && /mac|iphone|ipad/i.test(nav.platform)
    ? 'mac'
    : 'win';
}

/** 菜单右侧显示用的快捷键文案。 */
export function formatShortcut(shortcut: Shortcut, platform: Platform = detectPlatform()): string {
  if (platform === 'mac') {
    // macOS 惯例：修饰符号连写，顺序 ⌥⇧⌘
    return `${shortcut.alt === true ? '⌥' : ''}${shortcut.shift === true ? '⇧' : ''}${
      shortcut.mod === true ? '⌘' : ''
    }${shortcut.key.toUpperCase()}`;
  }
  const parts: string[] = [];
  if (shortcut.mod === true) parts.push('Ctrl');
  if (shortcut.alt === true) parts.push('Alt');
  if (shortcut.shift === true) parts.push('Shift');
  parts.push(shortcut.key.toUpperCase());
  return parts.join('+');
}

/**
 * §7.4 列出的动作快捷键（工具单字母键除外）。
 * test/app/actions.test.ts 内置同一张表逐条对拍——改这里必须连测试一起改，
 * 而那需要一个偏离 Photoshop 惯例的正当理由。
 */
export const PHOTOSHOP_ACTION_SHORTCUTS = {
  'edit.undo': { key: 'Z', mod: true },
  'edit.stepBackward': { key: 'Z', mod: true, alt: true },
  'edit.stepForward': { key: 'Z', mod: true, shift: true },
  'layer.duplicate': { key: 'J', mod: true },
  'layer.new': { key: 'N', mod: true, shift: true },
  'layer.group': { key: 'G', mod: true },
  'edit.freeTransform': { key: 'T', mod: true },
  'select.deselect': { key: 'D', mod: true },
  'select.reselect': { key: 'D', mod: true, shift: true },
  'select.invert': { key: 'I', mod: true, shift: true },
  'select.all': { key: 'A', mod: true },
  'file.new': { key: 'N', mod: true },
  'file.open': { key: 'O', mod: true },
  'file.save': { key: 'S', mod: true },
} as const satisfies Readonly<Record<string, Shortcut>>;
